import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { admin } from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import {
  ArrowLeft, Trophy, Gamepad2, Mail, Calendar, Shield, Crown, User,
  Ban, RefreshCw, UserCog, Gift, AlertTriangle, Clock,
} from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../../components/ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { toast } from 'sonner';
import { USER_ROLES, getGameName } from '../../constants/theme';

const getRoleBadgeColor = (role) => {
  const colors = {
    ADMIN: 'bg-red-500/20 text-red-400 border-red-500/30',
    EDITOR: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    ORG: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    PLAYER: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    VISITOR: 'bg-gray-500/20 text-gray-400 border-gray-500/30',
  };
  return colors[role] || colors.VISITOR;
};

export default function UserDetailPage() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [roleDialog, setRoleDialog] = useState(false);
  const [newRole, setNewRole] = useState('');

  useEffect(() => { load(); }, [userId]);

  const load = async () => {
    setLoading(true);
    try {
      const res = await admin.getUserDetails(userId);
      setData(res.data);
    } catch (e) { toast.error('Erreur'); navigate('/admin/users'); }
    finally { setLoading(false); }
  };

  const handleRoleChange = async () => {
    try {
      await admin.updateUserRole(userId, newRole);
      toast.success('Rôle mis à jour');
      setRoleDialog(false);
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleToggleArcanien = async () => {
    const current = data.user.is_arcanien;
    try {
      await admin.toggleArcanien(userId, { is_arcanien: !current });
      toast.success(current ? 'Arcanien retiré' : 'Arcanien accordé');
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleBanToggle = async () => {
    try {
      if (data.user.is_banned) {
        await admin.unbanUser(userId);
        toast.success('Débanni');
      } else {
        await admin.banUser(userId, 'Banni par admin');
        toast.success('Banni');
      }
      load();
    } catch (e) { toast.error('Erreur'); }
  };

  const handleResetPw = async () => {
    try {
      const res = await admin.resetPassword(userId);
      toast.success(`Mot de passe: ${res.data.new_password}`);
    } catch (e) { toast.error('Erreur'); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF6B35]" /></div>;
  if (!data) return null;

  const u = data.user;

  return (
    <div className="space-y-6">
      <Button variant="ghost" onClick={() => navigate('/admin/users')} className="text-[#B0B0B0] hover:text-white" data-testid="back-to-users">
        <ArrowLeft className="w-4 h-4 mr-2" />Retour
      </Button>

      {/* User Header */}
      <Card className="bg-[#1a1a1a] border-[#333]">
        <CardContent className="p-6">
          <div className="flex items-start gap-6">
            <div className="w-20 h-20 rounded-full bg-[#FF6B35]/20 flex items-center justify-center flex-shrink-0">
              {u.avatar_url ? <img src={u.avatar_url} className="w-20 h-20 rounded-full object-cover" alt="" /> : <User className="w-10 h-10 text-[#FF6B35]" />}
            </div>
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h1 className="text-2xl font-bold text-white" data-testid="user-detail-name">{u.name}</h1>
                <Badge className={getRoleBadgeColor(u.role)}>{USER_ROLES[u.role] || u.role}</Badge>
                {u.is_arcanien && <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30"><Crown className="w-3 h-3 mr-1" />Arcanien</Badge>}
                {u.is_banned && <Badge className="bg-red-500/20 text-red-400 border-red-500/30">Banni</Badge>}
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
                {u.pseudo && <div className="text-[#B0B0B0]"><span className="text-[#666]">Pseudo:</span> @{u.pseudo}</div>}
                <div className="text-[#B0B0B0]"><Mail className="w-3 h-3 inline mr-1" />{u.email}</div>
                <div className="text-[#B0B0B0]"><Calendar className="w-3 h-3 inline mr-1" />Inscrit le {new Date(u.created_at).toLocaleDateString('fr-FR')}</div>
                {u.city && <div className="text-[#B0B0B0]">{u.city}</div>}
              </div>
            </div>
            <div className="flex flex-col gap-2 flex-shrink-0">
              <Button size="sm" variant="outline" onClick={() => { setNewRole(u.role); setRoleDialog(true); }}
                className="border-[#333] text-[#B0B0B0] hover:text-white" data-testid="change-role-btn">
                <UserCog className="w-4 h-4 mr-2" />Rôle
              </Button>
              <Button size="sm" variant="outline" onClick={handleToggleArcanien}
                className="border-[#333] text-yellow-400 hover:text-yellow-300" data-testid="toggle-arcanien-btn">
                <Crown className="w-4 h-4 mr-2" />{u.is_arcanien ? 'Retirer Arcanien' : 'Donner Arcanien'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleBanToggle}
                className={`border-[#333] ${u.is_banned ? 'text-green-400' : 'text-red-400'}`}>
                <Ban className="w-4 h-4 mr-2" />{u.is_banned ? 'Débannir' : 'Bannir'}
              </Button>
              <Button size="sm" variant="outline" onClick={handleResetPw}
                className="border-[#333] text-[#4ECDC4] hover:text-[#4ECDC4]">
                <RefreshCw className="w-4 h-4 mr-2" />Reset MDP
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Stats Grid */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatCard title="Tournois inscrits" value={data.tournaments_registered.length} icon={Trophy} color="#FF6B35" />
        <StatCard title="Tournois organisés" value={data.tournaments_organized.length} icon={Trophy} color="#4ECDC4" />
        <StatCard title="Parties" value={data.availabilities.length} icon={Gamepad2} color="#7B68EE" />
        <StatCard title="Signalements" value={data.reports_by_user.length} icon={AlertTriangle} color="#EF4444" />
      </div>

      {/* Gifts */}
      {(data.gifts_given.length > 0 || data.gifts_received.length > 0) && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Gift className="w-5 h-5 text-teal-400" />Cadeaux Arcanien</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2">
              {data.gifts_given.map(g => (
                <div key={g.id} className="flex items-center gap-2 text-sm">
                  <Badge className="bg-teal-500/20 text-teal-400 border-teal-500/30">Offert</Badge>
                  <span className="text-[#B0B0B0]">à @{g.receiver_pseudo} - {g.duration_months} mois</span>
                  <span className="text-[#666] ml-auto">{new Date(g.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              ))}
              {data.gifts_received.map(g => (
                <div key={g.id} className="flex items-center gap-2 text-sm">
                  <Badge className="bg-yellow-500/20 text-yellow-400 border-yellow-500/30">Reçu</Badge>
                  <span className="text-[#B0B0B0]">de @{g.giver_pseudo} - {g.duration_months} mois</span>
                  <span className="text-[#666] ml-auto">{new Date(g.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tournaments */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Trophy className="w-5 h-5 text-[#FF6B35]" />Tournois inscrits</CardTitle></CardHeader>
          <CardContent>
            {data.tournaments_registered.length === 0 ? <p className="text-[#666] text-center py-4">Aucun</p> : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.tournaments_registered.map(t => (
                  <div key={t.id} className="flex items-center justify-between p-2 rounded-md bg-[#252525]">
                    <div><p className="text-white text-sm">{t.name}</p><p className="text-[#666] text-xs">{getGameName(t.game)} - {new Date(t.date).toLocaleDateString('fr-FR')}</p></div>
                    <Badge variant="outline" className="border-[#333] text-[#666] text-xs">{t.status}</Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Gamepad2 className="w-5 h-5 text-[#7B68EE]" />Parties</CardTitle></CardHeader>
          <CardContent>
            {data.availabilities.length === 0 ? <p className="text-[#666] text-center py-4">Aucune</p> : (
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {data.availabilities.map(a => (
                  <div key={a.id} className="flex items-center justify-between p-2 rounded-md bg-[#252525]">
                    <div><p className="text-white text-sm">{getGameName(a.game)}</p><p className="text-[#666] text-xs">{new Date(a.date).toLocaleDateString('fr-FR')}</p></div>
                    <Badge variant="outline" className={`border-[#333] text-xs ${a.user_id === userId ? 'text-[#FF6B35]' : 'text-[#666]'}`}>
                      {a.user_id === userId ? 'Créateur' : 'Participant'}
                    </Badge>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Recent Activity */}
      {data.registration_logs.length > 0 && (
        <Card className="bg-[#1a1a1a] border-[#333]">
          <CardHeader><CardTitle className="text-white flex items-center gap-2"><Clock className="w-5 h-5 text-[#4ECDC4]" />Activité récente</CardTitle></CardHeader>
          <CardContent>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {data.registration_logs.slice(0, 20).map(l => (
                <div key={l.id} className="flex items-center gap-3 text-sm">
                  <div className="w-2 h-2 rounded-full bg-[#4ECDC4] flex-shrink-0" />
                  <span className="text-[#B0B0B0]">{l.type === 'tournament' ? 'Inscription tournoi' : 'Inscription partie'}</span>
                  <span className="text-[#666] ml-auto">{new Date(l.created_at).toLocaleDateString('fr-FR')}</span>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Role Dialog */}
      <Dialog open={roleDialog} onOpenChange={setRoleDialog}>
        <DialogContent className="bg-[#1a1a1a] border-[#333] text-white">
          <DialogHeader><DialogTitle>Modifier le rôle de {u.name}</DialogTitle></DialogHeader>
          <Select value={newRole} onValueChange={setNewRole}>
            <SelectTrigger className="bg-[#252525] border-[#333] text-white"><SelectValue /></SelectTrigger>
            <SelectContent className="bg-[#252525] border-[#333]">
              {Object.entries(USER_ROLES).map(([k, v]) => (
                <SelectItem key={k} value={k} className="text-white">{v}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRoleDialog(false)} className="border-[#333] text-white">Annuler</Button>
            <Button onClick={handleRoleChange} className="bg-[#FF6B35] hover:bg-[#E55A25]">Enregistrer</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  return (
    <Card className="bg-[#1a1a1a] border-[#333]">
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs text-[#666]">{title}</p>
            <p className="text-2xl font-bold text-white mt-1">{value}</p>
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}20` }}>
            <Icon className="w-5 h-5" style={{ color }} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
